import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Modal,
  TextField,
  CircularProgress,
  Card,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import api, { API_BASE_URL } from "../../../utils/api";

const WorkOrderVisits = ({ work_order_id, currentUserId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [commentImageURL, setCommentImageURL] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/getEvents`);
        const filteredEvents = (response.data || []).filter(
          (event) => event.work_order_id === Number(work_order_id)
        );
        setEvents(filteredEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    if (work_order_id) {
      fetchEvents();
    }
  }, [work_order_id]);

  const fetchComments = async (eventId) => {
    setIsCommentsLoading(true);
    try {
      const response = await api.get(`/getComments/${work_order_id}/${eventId}`);
      setComments(response.data || []);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
      setComments([]);
    } finally {
      setIsCommentsLoading(false);
    }
  };

  const handleOpenComments = (eventId) => {
    setSelectedEventId(eventId);
    setIsModalOpen(true);
    setNewComment("");
    setSelectedFile(null);
    setFilePreview(null);
    fetchComments(eventId);
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setFilePreview(URL.createObjectURL(file));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      setSnackbar({
        open: true,
        message: "Comment cannot be empty.",
        severity: "warning",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append("work_order_id", work_order_id);
      formData.append("event_id", selectedEventId);
      formData.append("user_id", currentUserId);
      formData.append("comment_text", newComment);
      formData.append("is_internal", false);

      if (selectedFile) {
        formData.append("upload_urls", selectedFile);
      }

      await api.post(`/addComment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      await fetchComments(selectedEventId);

      setNewComment("");
      setSelectedFile(null);
      setFilePreview(null);

      setSnackbar({
        open: true,
        message: "Comment added successfully.",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to add comment:", error);
      setSnackbar({
        open: true,
        message: "Failed to add comment. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const modalPaperSx = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "92%", sm: 560 },
    maxHeight: "90vh",
    overflow: "auto",
    backgroundColor: "rgba(255,255,255,0.98)",
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "180px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
          No visits found
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
          There are no visits associated with this work order.
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <EventAvailableIcon sx={{ color: "#2f63ad" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Visits
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Review scheduled visits and add comments for each event.
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: "14px",
              border: "1px solid rgba(47,99,173,0.08)",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f7f9fc",
                    "& .MuiTableCell-root": {
                      fontWeight: 700,
                      color: "#223142",
                    },
                  }}
                >
                  <TableCell>Event ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {events.map((event, index) => (
                  <TableRow
                    key={event.id}
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(47,99,173,0.03)",
                      },
                      "& .MuiTableCell-root": {
                        borderBottom:
                          index === events.length - 1
                            ? "none"
                            : "1px solid rgba(47,99,173,0.06)",
                        color: "#223142",
                      },
                    }}
                  >
                    <TableCell>{event.id}</TableCell>
                    <TableCell>{event.title || "N/A"}</TableCell>
                    <TableCell>{event.description || "N/A"}</TableCell>
                    <TableCell>
                      {event.start_time
                        ? new Date(event.start_time).toLocaleString()
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {event.end_time
                        ? new Date(event.end_time).toLocaleString()
                        : "N/A"}
                    </TableCell>
                    <TableCell>{event.created_by_user_id || "N/A"}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleOpenComments(event.id)}
                        sx={secondaryButtonSx}
                      >
                        View Comments
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Card>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="comments-modal"
      >
        <Box sx={modalPaperSx}>
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              backgroundColor: "rgba(47,99,173,0.02)",
            }}
          >
            <Box>
              <Typography
                id="comments-modal"
                variant="h6"
                sx={{ fontWeight: 700, color: "#223142" }}
              >
                Visit Comments
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.4 }}>
                Work Order ID: {work_order_id} • Event ID: {selectedEventId}
              </Typography>
            </Box>

            <IconButton onClick={() => setIsModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3 }}>
            {isCommentsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress />
              </Box>
            ) : comments.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {comments.map((comment, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1.5,
                      border: "1px solid rgba(47,99,173,0.08)",
                      borderRadius: "12px",
                      backgroundColor: "rgba(47,99,173,0.02)",
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 0.75, color: "#223142" }}>
                      {comment.comment_text}
                    </Typography>

                    {comment.upload_urls && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#223142", mb: 1 }}
                        >
                          Attachment
                        </Typography>

                        <Card
                          elevation={0}
                          sx={{
                            p: 1,
                            maxWidth: 220,
                            borderRadius: "12px",
                            border: "1px solid rgba(47,99,173,0.08)",
                            boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setCommentImageURL(
                              `${API_BASE_URL}/workordercommentimages/${encodeURIComponent(
                                comment.upload_urls
                              )}`
                            );
                            setOpenImageModal(true);
                          }}
                        >
                          <img
                            src={`${API_BASE_URL}/workordercommentimages/${encodeURIComponent(
                              comment.upload_urls
                            )}`}
                            alt="Comment Attachment"
                            style={{
                              width: "100%",
                              borderRadius: "8px",
                              display: "block",
                            }}
                          />
                        </Card>
                      </Box>
                    )}

                    <Typography variant="caption" sx={{ color: "#6b778c" }}>
                      Added by {comment.user_name} {comment.user_lastname || ""} on{" "}
                      {comment.sent_at
                        ? new Date(comment.sent_at).toLocaleString()
                        : "N/A"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
                No comments available.
              </Typography>
            )}

            <TextField
              fullWidth
              label="Add a Comment"
              multiline
              rows={4}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{ ...textFieldSx, mt: 2, mb: 2 }}
            />

            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed #90caf9",
                borderRadius: "14px",
                p: 2.5,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragActive ? "#e3f2fd" : "#fafafa",
                transition: "all 0.2s ease",
                mb: 2,
                "&:hover": {
                  backgroundColor: "#f5faff",
                  borderColor: "#1976d2",
                },
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {isDragActive
                  ? "Drop image here..."
                  : "Drag & drop an image here, or click to select"}
              </Typography>
            </Box>

            {filePreview && (
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ color: "#223142", mb: 1 }}>
                  Image Preview
                </Typography>
                <Box
                  component="img"
                  src={filePreview}
                  alt="Preview"
                  sx={{
                    maxWidth: "100%",
                    maxHeight: 220,
                    borderRadius: "12px",
                    border: "1px solid rgba(47,99,173,0.08)",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                  }}
                />
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={handleAddComment}
                disabled={isSubmitting}
                sx={primaryButtonSx}
              >
                {isSubmitting ? "Saving..." : "Save Comment"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={openImageModal}
        onClose={() => setOpenImageModal(false)}
        aria-labelledby="comment-image-preview"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxWidth: "90vw",
            maxHeight: "90vh",
            outline: "none",
          }}
        >
          <Box sx={{ position: "relative", bgcolor: "black", borderRadius: 2, overflow: "hidden" }}>
            <IconButton
              onClick={() => setOpenImageModal(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                zIndex: 1,
                bgcolor: "rgba(0,0,0,0.5)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
            >
              <CloseIcon sx={{ color: "white" }} />
            </IconButton>

            {commentImageURL && (
              <Box
                component="img"
                src={commentImageURL}
                alt="Comment Attachment"
                sx={{
                  display: "block",
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  objectFit: "contain",
                  backgroundColor: "black",
                }}
              />
            )}
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 3 }}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkOrderVisits;